import { ApiLinkEntity, ApiLinkRel, ApiServiceName } from "config/constants"
import ApiService from "services/ApiService"
import ErrorHandler from "utils/errorHandler"
import { IUserStorage, IUserStorageReadResult } from "../types"

export default class UserStorageService {

    /**
     * Reads a user setting.
     *
     * The setting is stored as an opaque string, so the response is a JSON string whose content has to
     * be parsed a second time to get the value back.
     *
     * @returns a result that keeps "nothing stored yet" apart from "stored but unreadable", so callers
     * can avoid overwriting a value they failed to read.
     */
    static async getValue<K extends keyof IUserStorage>(key: K): Promise<IUserStorageReadResult<K>> {
        try {
            const resp: string | IUserStorage[K] | null = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.userSettings,
                    rel: ApiLinkRel.self,
                    params: { key },
                }
            })
            if (!resp) return { status: "empty" }
            if (typeof resp !== "string") return { status: "loaded", value: resp }
            try {
                return { status: "loaded", value: JSON.parse(resp) }
            } catch (e) {
                console.error(e)
                return { status: "unreadable" }
            }
        } catch (e) {
            console.error(e)
            ErrorHandler.handleHttpError(e)
        }
    }

    /**
     * Persists a user setting.
     *
     * The endpoint binds the payload as a plain string, so the request body has to be a JSON string
     * literal wrapping the serialized value — which is why it is encoded twice. Both steps are needed:
     * axios forwards a string body untouched only when it is already valid JSON, and otherwise encodes
     * it again, so anything hand-wrapped here would end up inside the stored value.
     */
    static async setValue<K extends keyof IUserStorage>(key: K, data: IUserStorage[K]) {
        try {
            await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.userSettings,
                    rel: ApiLinkRel.update,
                    params: { key },
                },
                headers: {
                    "Content-Type": "application/json"
                },
                data: JSON.stringify(JSON.stringify(data)),
            })
        } catch (e) {
            console.error(e)
            ErrorHandler.handleHttpError(e)
        }
    }

}
